import { createReducer, on, createFeature } from '@ngrx/store'
import { PayrollState } from './payroll.state'
import {
  payrollBatchesActions,
  payrollSummaryActions,
  resetPayrollSummary,
  runningPayrollActions,
  tdsCalculationActions,
  userPayrollActions
} from './payroll.actions'

const initialState: PayrollState = {
  payrollBatches: null,
  runningPayroll: null,
  previousSalaries: null,
  arrearLopDays: null,
  tdsCalculationBatch: null,
  tdsCalculation: null,
  isTdsPreviewLoading: false,
  userPayrollData: null,
  latestUserPayrollDate: null,
  PayrollSummary: null,
  loading: false,
  error: null
}

export const payrollReducer = createReducer(
  initialState,
  on(payrollBatchesActions.loadPayrollBatchesSuccess, (state, { payrollBatches }) => ({
    ...state,
    payrollBatches
  })),
  on(runningPayrollActions.setupRunningPayroll, (state, { batchData }) => ({
    ...state,
    runningPayroll: batchData
  })),
  on(runningPayrollActions.clearRunningPayroll, state => ({
    ...state,
    runningPayroll: null,
    previousSalaries: null,
    arrearLopDays: null
  })),
  on(runningPayrollActions.loadRunningPayroll, state => ({
    ...state,
    runningPayroll: null, // 🔥 clear old data immediately
    error: null
  })),

  on(runningPayrollActions.loadRunningPayrollFailure, (state, { error }) => ({
    ...state,
    runningPayroll: null, // 🔥 clear stale data
    error
  })),
  on(runningPayrollActions.loadRunningPayrollSuccess, (state, { updatedBatch }) => ({
    ...state,
    runningPayroll: updatedBatch
  })),
  on(runningPayrollActions.loadPreviousSalariesSuccess, (state, { previousSalaries }) => ({
    ...state,
    previousSalaries
  })),
  on(runningPayrollActions.clearPreviousSalaries, state => ({
    ...state,
    previousSalaries: null
  })),
  on(runningPayrollActions.loadArrearLopDaysSuccess, (state, { arrearLopDays }) => ({
    ...state,
    arrearLopDays
  })),
  on(runningPayrollActions.clearArrearLopDays, state => ({
    ...state,
    arrearLopDays: null
  })),
  on(tdsCalculationActions.loadBatchTdsPreviewSuccess, (state, tdsResults) => ({
    ...state,
    tdsCalculationBatch: tdsResults
  })),
  on(tdsCalculationActions.loadTdsPreviewSuccess, (state, tdsResult) => ({
    ...state,
    tdsCalculation: tdsResult
  })),
  on(runningPayrollActions.saveRunningPayrollDraftSuccess, (state, { batchData }) => ({
    ...state,
    runningPayroll: batchData
  })),
  on(runningPayrollActions.updateRunningPayrollStatusSuccess, (state, { batchData }) => ({
    ...state,
    runningPayroll: batchData
  })),
  on(payrollBatchesActions.deletePayrollBatchSuccess, (state, { id }) => {
    const updatedBatches = state.payrollBatches?.filter(batch => batch.id !== id) || []
    return {
      ...state,
      payrollBatches: updatedBatches.length > 0 ? updatedBatches : null
    }
  }),
  on(userPayrollActions.loadUserPayroll, state => ({
    ...state,
    userPayrollData: null,
    error: null
  })),

  on(userPayrollActions.loadUserPayrollFailure, (state, { error }) => ({
    ...state,
    userPayrollData: null, // 🔥 clear old payroll
    error
  })),

  on(userPayrollActions.loadUserPayrollSuccess, (state, { payroll }) => ({
    ...state,
    userPayrollData: payroll
  })),
  on(userPayrollActions.loadLatestUserPayrollDateSuccess, (state, { latestPayrollDate }) => ({
    ...state,
    latestUserPayrollDate: latestPayrollDate
  })),
  on(userPayrollActions.loadLatestUserPayrollDateFailure, (state, { error }) => ({
    ...state,
    latestUserPayrollDate: null,
    error
  })),
  on(payrollBatchesActions.finalizePayrollSuccess, (state, { batch }) => ({
    ...state,
    payrollBatches:
      state.payrollBatches?.map(batchData => {
        if (batchData.id === batch.id) {
          return batch
        }
        return batchData
      }) ?? null
  })),
  on(payrollSummaryActions.loadSummary, state => ({
    ...state,
    payrollSummary: null, // clear old data
    loading: true
  })),
  on(payrollSummaryActions.loadSummarySuccess, (state, { summary }) => ({
    ...state,
    PayrollSummary: summary,
    loading: false
  })),
  on(resetPayrollSummary, state => ({
    ...state,
    payrollSummary: null // or [] depending on your type
  }))
)

export const payrollFeature = createFeature({
  name: 'payroll',
  reducer: payrollReducer
})

export const {
  selectPayrollBatches,
  selectRunningPayroll,
  selectPreviousSalaries,
  selectArrearLopDays,
  selectTdsCalculationBatch,
  selectTdsCalculation,
  selectIsTdsPreviewLoading,
  selectUserPayrollData,
  selectLatestUserPayrollDate,
  selectPayrollSummary,
  selectLoading,
  selectError
} = payrollFeature
